import type { TaxDetail, HotelRoom, RateOption } from '@/types/hotel-search';

/**
 * Parse a Booking.com price string into a number.
 *
 * Handles pt-BR format where dot is the thousands separator:
 *   "R$ 1.484"     → 1484
 *   "R$ 1.484,30"  → 1484.30
 *   "R$ 2.962"     → 2962
 *
 * Also handles en format where comma is the thousands separator:
 *   "R$ 1,484"     → 1484
 *   "R$ 1,484.30"  → 1484.30
 */
export function parsePtBrPrice(str: string): number | null {
  if (!str) return null;
  const cleaned = str.replace(/[^\d.,]/g, '').trim();
  if (!cleaned) return null;

  const hasComma = cleaned.includes(',');
  const hasDot = cleaned.includes('.');

  if (hasComma && hasDot) {
    const lastComma = cleaned.lastIndexOf(',');
    const lastDot = cleaned.lastIndexOf('.');
    if (lastComma > lastDot) {
      // pt-BR: "1.484,30" — dot=thousands, comma=decimal
      return parseFloat(cleaned.replace(/\./g, '').replace(',', '.'));
    } else {
      // en: "1,484.30" — comma=thousands, dot=decimal
      return parseFloat(cleaned.replace(/,/g, ''));
    }
  }

  if (hasComma) {
    const afterComma = cleaned.split(',').slice(-1)[0];
    if (afterComma.length <= 2) {
      // "1484,30" — comma is decimal separator
      return parseFloat(cleaned.replace(',', '.'));
    }
    // "1,484" — comma is thousands separator (en)
    return parseFloat(cleaned.replace(/,/g, ''));
  }

  if (hasDot) {
    const parts = cleaned.split('.');
    const afterDot = parts.slice(1).join('');
    if (parts.length === 2 && afterDot.length === 3) {
      // "1.484" — dot is thousands separator (pt-BR); 3-digit fractional part = thousands
      return parseFloat(cleaned.replace(/\./g, ''));
    }
    // "1484.30" or "1.50" — dot is decimal
    return parseFloat(cleaned);
  }

  const n = parseFloat(cleaned);
  return isNaN(n) ? null : n;
}

/**
 * Compute the compound tax multiplier from a rate option's tax details.
 *
 * Only `excluded` percentage taxes are applied (taxes not already in the price).
 * Deduplicates by `raw` string to avoid double-counting scraping artifacts.
 *
 * Example: two 10% excluded taxes → 1.1 × 1.1 = 1.21 (not 1.20)
 */
export function computeTaxMultiplier(taxDetails: TaxDetail[]): number {
  const seen = new Set<string>();
  let multiplier = 1;
  for (const tax of taxDetails) {
    if (tax.category !== 'excluded') continue;
    if (tax.basis !== 'percentage') continue;
    if (!tax.percentage || tax.percentage <= 0) continue;
    if (seen.has(tax.raw)) continue;
    seen.add(tax.raw);
    multiplier *= 1 + tax.percentage / 100;
  }
  return multiplier;
}

export interface GrossPriceSummary {
  currency: string;
  /** Lowest gross price (taxes included) */
  min: number;
  /** Highest gross price (taxes included) */
  max: number;
  /** Base price (before taxes) for the cheapest option */
  baseMin: number;
  /** Base price (before taxes) for the most expensive option */
  baseMax: number;
  /** Tax amount added to the cheapest option (gross.min - base.min) */
  taxMin: number;
  /** Tax multiplier of the cheapest option (e.g. 1.21 for 2×10%) */
  multiplier: number;
  /** True when at least one rate option has excluded percentage taxes */
  hasTaxes: boolean;
}

/**
 * Compute the gross (taxes-included) price summary from all rate options.
 *
 * Priority order per rate option:
 *   1. `taxes_included=true` → `price` is already gross, use it directly.
 *   2. `gross_price` non-empty → API pre-calculated gross (price + aggregate tax), use it.
 *   3. Fallback → apply compound multiplier from `excluded` percentage taxes.
 *
 * Returns null if no parseable prices exist.
 */
export function computeGrossPriceSummary(
  rooms: HotelRoom[],
  currency: string,
): GrossPriceSummary | null {
  let minGross = Infinity;
  let maxGross = -Infinity;
  let minBase = Infinity;
  let maxBase = -Infinity;
  let minMultiplier = 1;
  let hasTaxes = false;

  for (const room of rooms) {
    for (const ro of room.rate_options) {
      const base = parsePtBrPrice(ro.total || ro.price);
      if (base == null || base <= 0) continue;

      let gross: number;
      let mult: number;

      if (ro.taxes_included) {
        // Price already includes all taxes — use as-is
        gross = base;
        mult = 1;
      } else if (ro.gross_price) {
        // API pre-calculated the gross (price + aggregate monetary tax)
        const preCalc = parsePtBrPrice(ro.gross_price);
        if (preCalc != null && preCalc > 0) {
          gross = preCalc;
          mult = preCalc / base;
          hasTaxes = true;
        } else {
          gross = base;
          mult = 1;
        }
      } else {
        // Fallback: apply compound multiplier from excluded percentage taxes
        mult = computeTaxMultiplier(ro.taxes_and_charges_details);
        gross = base * mult;
        if (mult > 1) hasTaxes = true;
      }

      if (gross < minGross) { minGross = gross; minBase = base; minMultiplier = mult; }
      if (gross > maxGross) { maxGross = gross; maxBase = base; }
    }
  }

  if (!isFinite(minGross)) return null;
  return {
    currency,
    min: minGross,
    max: maxGross,
    baseMin: minBase,
    baseMax: maxBase,
    taxMin: minGross - minBase,
    multiplier: minMultiplier,
    hasTaxes,
  };
}

/**
 * Compute gross price for a single rate option (used in detail modal per-row).
 *
 * Priority order:
 *   1. `taxes_included=true` → base price is already gross.
 *   2. `gross_price` non-empty → use API pre-calculated value.
 *   3. Fallback → apply compound multiplier from excluded percentage taxes.
 *
 * Returns null if the base price is not parseable.
 */
export function computeGrossForRate(
  rateOption: RateOption,
): { gross: number; multiplier: number } | null {
  const base = parsePtBrPrice(rateOption.total || rateOption.price);
  if (base == null || base <= 0) return null;

  if (rateOption.taxes_included) {
    return { gross: base, multiplier: 1 };
  }

  if (rateOption.gross_price) {
    const preCalc = parsePtBrPrice(rateOption.gross_price);
    if (preCalc != null && preCalc > 0) {
      return { gross: preCalc, multiplier: preCalc / base };
    }
  }

  const multiplier = computeTaxMultiplier(rateOption.taxes_and_charges_details);
  if (multiplier <= 1) return null;
  return { gross: base * multiplier, multiplier };
}

/**
 * Format a gross price for display in the same visual style as the existing prices.
 * Uses pt-BR locale and no decimal places (hotel prices are always round numbers).
 */
export function formatGrossPrice(value: number): string {
  return Math.round(value).toLocaleString('pt-BR');
}

/**
 * Describe the tax multiplier as a compact human-readable string.
 * e.g. 1.21 → "+21%", 1.10 → "+10%"
 */
export function multiplierLabel(mult: number): string {
  const pct = Math.round((mult - 1) * 100);
  return `+${pct}%`;
}
